import React, { useCallback, useState } from 'react';
import {
  getLocaleDateTimeString,
  history,
  timeForHumans,
  toTitleCase,
} from '../../../utils';
import StarRatingComponent from 'react-star-rating-component';
import EndRental from './endRental';
import ChargeModal from './chargeModal';
import { IRentalResumeProps } from '../../../ts/interface/pages/rentals.interface';
import { PropertyList } from '../../../components/Redesing/property-list';
import { PropertyListItem } from '../../../components/Redesing/property-list-item';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CardView from '../../../components/Redesing/card-view';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ChangeOrganizationToSystem from '../../../hooks/system/hook/ChangeOrganizationToSystem';
import { ISystem } from '../../../ts/interface/system.interface';
import useOrganization from '../../../hooks/use-organization';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import useResolveRentalReview from '../../../hooks/rentals/mutation/useResolveRentalReview';
import { ButtonInfo } from '../../../ts/interface/components/redesing/table.interface';

const RentalResume = ({
  system,
  rental,
  auth,
  modeInfo,
  rentalAdminPermission,
  systemLoaded,
  onRentalEnded,
  showSystem = false,
  isLoading = false,
  movaticAccess = false,
  reviewStatus = null,
  onReviewResolved,
}: IRentalResumeProps) => {
  const organizationInfo = useOrganization();

  const [chargeRentalModal, setChargeRentalModal] = useState<boolean>(false);
  const [endRentalModal, setEndRentalModal] = useState<boolean>(false);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected' | null>(
    null
  );
  const [reviewSubmitting, setReviewSubmitting] = useState<boolean>(false);

  const { mutateAsync: resolveReviewAsync } = useResolveRentalReview();

  const submitReviewDecision = (decision: 'approved' | 'rejected') => {
    setReviewSubmitting(true);
    CustomAsyncToast({
      promise: () => resolveReviewAsync({ rentalId: rental.id, decision }),
      successMessage: () =>
        decision === 'approved' ? 'Review approved.' : 'Review rejected.',
      errorMessage: 'There was an error resolving the review. Please try again later.',
      loadingMessage: 'Submitting review decision...',
    })
      .then((response) => {
        if (response) {
          setReviewDecision(null);
          // Refetch so the review chip reflects the new resolution.
          onReviewResolved?.();
        }
      })
      .catch(() => {
        // Error is surfaced via the toast above; swallow to avoid an unhandled
        // rejection and keep the confirmation dialog open for a retry.
      })
      .finally(() => {
        setReviewSubmitting(false);
      });
  };

  // Informational pill for the rental's review state. null reviewStatus means
  // a legacy rental with no review, so nothing is rendered.
  const getReviewPill = () => {
    switch (reviewStatus) {
      case 'pending':
        return <SeverityPill color="warning">Pending review</SeverityPill>;
      case 'approved':
        return <SeverityPill color="success">Review approved</SeverityPill>;
      case 'rejected':
        return <SeverityPill color="error">Review rejected</SeverityPill>;
      default:
        return null;
    }
  };

  const reviewPill = getReviewPill();

  const getReviewConfirmModal = () => {
    if (!reviewDecision) return null;
    const decision = reviewDecision;
    const approving = decision === 'approved';
    return (
      <MovaticCustomModal
        dataId="resolve-review-modal"
        open={Boolean(reviewDecision)}
        isLoading={reviewSubmitting}
        onClose={() => setReviewDecision(null)}
        title={approving ? 'Approve rental review' : 'Reject rental review'}
        content={
          approving
            ? 'Are you sure you want to approve this rental review?'
            : 'Are you sure you want to reject this rental review?'
        }
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setReviewDecision(null),
                startIcon: <CloseIcon />,
                disabled: reviewSubmitting,
              },
              {
                label: approving ? 'Approve' : 'Reject',
                onClick: () => submitReviewDecision(decision),
                startIcon: <CheckIcon />,
                disabled: reviewSubmitting,
              },
            ]}
          />
        }
      />
    );
  };

  const canBillForceEndedRental =
    system.billing &&
    !rental?.ended_within_grace_period &&
    !rental?.billing?.total &&
    rental?.force_end?.type?.toLowerCase() === 'user';

  const { duration, distance, end, hardware, rating, start, force_end, user } = rental;
  const { stationsTitle } = modeInfo.details;

  const transactionId = rental.billing?.transactions?.find(
    ({ type, source }) => type === 'charge' && source === 'stripe'
  );

  const getModal = () => {
    if (endRentalModal) {
      return (
        <EndRental
          rental={rental}
          showModal={endRentalModal}
          onClose={() => {
            setEndRentalModal(!endRentalModal);
            onRentalEnded?.();
          }}
          round_trip={system.round_trip}
          currentMode={modeInfo.currentMode}
          systemLoaded={systemLoaded}
          stationTitle={modeInfo.details.stationsTitle || 'Location'}
          billing={system.billing}
        />
      );
    }
    if (chargeRentalModal) {
      return (
        <ChargeModal
          showModal={chargeRentalModal}
          onClose={() => setChargeRentalModal(!chargeRentalModal)}
          userName={rental?.user?.full_name}
          billing={system.billing}
          rentalId={rental.old_id}
        />
      );
    }
    return null;
  };

  const renderLink = (to: string, text: string | number, dataId?: string) => (
    <Link onClick={() => history.push(to)} data-id={`${dataId}-link`}>
      <Typography color="text.secondary" variant="subtitle1">
        {text}
        <i id="table_item_carot" className="fa fa-caret-right" />
      </Typography>
    </Link>
  );

  const renderPropertyListItem = (
    divider: boolean,
    label: string,
    value: JSX.Element
  ) => (
    <PropertyListItem divider={divider} label={label}>
      {value}
    </PropertyListItem>
  );

  const getEndByStatus = useCallback(() => {
    const endBy: string = rental.end_by || '';
    if (endBy === '') {
      return '';
    }
    const matchDate =
      rental.end && rental.end.time ? new Date(rental.end.time) : new Date();
    const isOverdue: boolean = matchDate > new Date(endBy);
    return (
      <SeverityPill color={isOverdue ? 'error' : 'success'}>
        {getLocaleDateTimeString(rental.end_by)}
      </SeverityPill>
    );
  }, [rental?.end_by, rental.end]);

  // Compose the header menu: the existing charge/end action plus the review
  // approve/reject actions, which only appear for a pending review when the
  // admin has movatic access.
  const headerButtons: ButtonInfo[] = [];
  if (canBillForceEndedRental) {
    headerButtons.push({
      label: 'Charge Rental',
      onClick: () => setChargeRentalModal(!chargeRentalModal),
      startIcon: <PaymentIcon />,
    });
  } else if (!rental.end?.time) {
    headerButtons.push({
      label: 'End Rental',
      onClick: () => setEndRentalModal(!endRentalModal),
      startIcon: <CheckCircleIcon />,
    });
  }
  if (reviewStatus === 'pending' && movaticAccess) {
    headerButtons.push({
      label: 'Approve review',
      onClick: () => setReviewDecision('approved'),
      startIcon: <ThumbUpIcon />,
      dataId: 'approve-review',
    });
    headerButtons.push({
      label: 'Reject review',
      onClick: () => setReviewDecision('rejected'),
      startIcon: <ThumbDownIcon />,
      dataId: 'reject-review',
    });
  }

  return (
    // <Card>
    <CardView
      isLoading={isLoading}
      headerActions={
        headerButtons.length ? <IconButtonMenu buttons={headerButtons} /> : undefined
      }
      content={
        <>
          <PropertyList>
            {reviewPill && (
              <PropertyListItem divider label={'Review'} dataId={'review-status'}>
                {reviewPill}
              </PropertyListItem>
            )}
            {transactionId?.dispute_id && (
              <PropertyListItem divider label={'Disputed'}>
                {renderLink(
                  `/billing/disputes/${transactionId.dispute_id}`,
                  'Click To View'
                )}
              </PropertyListItem>
            )}
            <PropertyListItem divider label={'User'}>
              {renderLink(`/users/${user?.phone_number}/general`, user?.full_name || '')}
            </PropertyListItem>
            {start?.triggered_by_type &&
              renderPropertyListItem(
                true,
                'Started by',
                toTitleCase(start.triggered_by_type)
              )}
            {end?.triggered_by_type &&
              renderPropertyListItem(
                true,
                'Ended by',
                toTitleCase(end.triggered_by_type)
              )}
            {system?.new_hardware || Boolean(organizationInfo?.id) ? (
              <PropertyListItem
                divider
                label={`${toTitleCase(hardware?.type_name || '')} Number`}
              >
                {renderLink(`/hardware/${hardware?.id || ''}`, hardware?.number || '')}
              </PropertyListItem>
            ) : (
              <PropertyListItem
                divider
                label={`${toTitleCase(hardware?.type_name || '')} Number`}
              >
                {renderLink(
                  `/units/${hardware?.type_name}/${hardware?.old_id || ''}`,
                  hardware?.number || ''
                )}
              </PropertyListItem>
            )}
            {start?.station?.id && start.station.id === end?.station?.id && (
              <PropertyListItem divider label={stationsTitle || ''}>
                {renderLink(
                  `/locations/${start.station.id}/general`,
                  start.station.name || ''
                )}
              </PropertyListItem>
            )}
            {start?.station?.id !== end?.station?.id && (
              <>
                <PropertyListItem
                  divider
                  label={`Start ${stationsTitle || ''}`}
                  dataId={`startStation`}
                >
                  {start?.station?.id ? (
                    renderLink(
                      `/locations/${start.station.id}/general`,
                      start?.station?.name,
                      `startStation`
                    )
                  ) : (
                    <SeverityPill color={'info'}>None</SeverityPill>
                  )}
                </PropertyListItem>
                {!!end?.time && (
                  <PropertyListItem
                    divider
                    label={`End ${stationsTitle || ''}`}
                    dataId={`endStation`}
                  >
                    {end?.station?.id ? (
                      renderLink(
                        `/locations/${end.station.id}/general`,
                        `${end.station.name} `,
                        `endStation`
                      )
                    ) : (
                      <SeverityPill color={'info'}>None</SeverityPill>
                    )}
                  </PropertyListItem>
                )}
              </>
            )}
            <PropertyListItem
              label={'Start Time'}
              value={getLocaleDateTimeString(start?.time)}
            />
            {end?.time && (
              <>
                <Divider />
                <PropertyListItem
                  label={'End Time'}
                  value={getLocaleDateTimeString(end?.time)}
                />
              </>
            )}
            {Boolean(rental.end_by) && (
              <PropertyListItem
                align={'horizontal'}
                label="End by"
                dataId="end-by"
                value={<div>{getEndByStatus()}</div>}
              />
            )}
            {duration ? (
              <>
                <Divider />
                <PropertyListItem
                  dataId={'rental-duration'}
                  label={'Duration'}
                  value={timeForHumans(duration).toString()}
                />
              </>
            ) : (
              <>
                <Divider />
                <PropertyListItem
                  dataId={'rental-duration'}
                  label={'Duration'}
                  value={'Rental is ongoing'}
                />
              </>
            )}
            {distance &&
            distance.kilometers &&
            system?.country?.toLowerCase() !== 'us' ? (
              <>
                <Divider />
                <PropertyListItem
                  label={'Distance'}
                  value={`${distance.kilometers.toFixed(3)} km`}
                />
              </>
            ) : (
              <>
                {distance && distance.miles ? (
                  <>
                    <Divider />
                    <PropertyListItem
                      label={'Distance'}
                      value={`${distance.miles.toFixed(3)} mi`}
                    />
                  </>
                ) : null}
              </>
            )}
            {force_end?.time && (
              <>
                <Divider />
                <PropertyListItem
                  label={'Force ended'}
                  value={
                    force_end.type?.toLowerCase() === 'user'
                      ? 'Rental was ended by another user checking out the bike.'
                      : `Rental was ended by an admin on ${getLocaleDateTimeString(
                          force_end.time
                        )}`
                  }
                />
              </>
            )}
            {end?.time || rating ? (
              <>
                <Divider />
                <PropertyListItem label={'Rating'}>
                  {typeof rating === 'number' ? (
                    <StarRatingComponent
                      name="rating"
                      value={rating}
                      starCount={5}
                      editing={false}
                    />
                  ) : (
                    <SeverityPill color="info">No Rating</SeverityPill>
                  )}
                </PropertyListItem>
              </>
            ) : null}

            {showSystem && (
              <>
                <Divider />
                <ChangeOrganizationToSystem system={system || ({} as ISystem)} />
              </>
            )}
          </PropertyList>
          {getModal()}
          {getReviewConfirmModal()}
        </>
      }
      title={
        <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
          Rental Details
        </div>
      }
    />
  );
};

export default RentalResume;
